import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  SegmentTabs,
  unwrapTrpcData,
} from '@org/ui';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getMutationErrorMessage } from '../shared/multi-step-utils';
import {
  REVIEW_FILTERS,
  countPending,
  filterReviews,
  identityStatusKey,
  identityStatusTone,
  sortReviews,
  studentLabel,
  toDate,
  verdictPercent,
  type IdentityReviewRow,
  type ReviewFilter,
} from './identity-review-helpers';

type ReviewRow = RouterOutputs['identity']['listForReview'][number];

const toneClass: Record<ReturnType<typeof identityStatusTone>, string> = {
  pending: 'border-amber-400 bg-amber-50 text-amber-900',
  declined: 'border-red-300 bg-red-50 text-red-800',
  verified: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  none: 'border-border bg-muted text-muted-foreground',
};

/** Status chip shared by the review page, the students list and the Access dialog. */
export function IdentityStatusChip({ status }: { status: IdentityReviewRow['identityStatus'] | null | undefined }) {
  const { t } = useTranslation('admin');
  return (
    <Badge variant="outline" className={toneClass[identityStatusTone(status)]}>
      {t(`identityReviews.status.${identityStatusKey(status)}`)}
    </Badge>
  );
}

/**
 * Self sign-ups whose photo needs a human decision. Each card shows the AI's
 * verdict; opening it shows the photo large with Approve / Decline.
 */
export function IdentityReviewList() {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.identity.listForReview.useQuery();
  const rows = useMemo(
    () => sortReviews((unwrapTrpcData<ReviewRow[]>(data as ReviewRow[]) ?? []) as IdentityReviewRow[]),
    [data],
  );
  const pendingCount = countPending(rows);
  const [filter, setFilter] = useState<ReviewFilter>('all');
  const visibleRows = useMemo(() => filterReviews(rows, filter), [rows, filter]);

  const [openRow, setOpenRow] = useState<IdentityReviewRow | null>(null);
  const [pendingDecline, setPendingDecline] = useState<IdentityReviewRow | null>(null);

  const invalidate = async () => {
    await utils.identity.listForReview.invalidate();
    await utils.users.listStudentsWithDetails.invalidate();
  };

  const reviewMutation = trpc.identity.review.useMutation({
    onError: (error: unknown) =>
      toast.error(t('identityReviews.toasts.error', { message: getMutationErrorMessage(error) })),
  });

  const decide = async (row: IdentityReviewRow, decision: 'approve' | 'decline') => {
    try {
      await reviewMutation.mutateAsync({ userId: row.userId, decision });
      toast.success(
        decision === 'approve'
          ? t('identityReviews.toasts.approved', { name: studentLabel(row) })
          : t('identityReviews.toasts.declined', { name: studentLabel(row) }),
      );
      setOpenRow(null);
      await invalidate();
    } catch {
      // onError already reported it.
    }
  };

  const formatDate = (value: unknown) => {
    const date = toDate(value);
    return date ? format(date, 'PPp') : '';
  };

  const verdictLine = (row: IdentityReviewRow) => {
    if (!row.aiVerdict) return t('identityReviews.verdict.none');
    const pct = verdictPercent(row.aiVerdict);
    return t(`identityReviews.verdict.${row.aiVerdict.verdict}`, { percent: pct ?? 0 });
  };

  return (
    <div className="px-6 pb-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('identityReviews.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('identityReviews.description')}</p>
        </div>
        <Badge variant={pendingCount > 0 ? 'default' : 'outline'}>
          {t('identityReviews.pendingCount', { count: pendingCount })}
        </Badge>
      </div>

      <SegmentTabs
        className="mb-4"
        value={filter}
        onValueChange={(value) => setFilter(value as ReviewFilter)}
        aria-label={t('identityReviews.title')}
        items={REVIEW_FILTERS.map((value) => ({
          value,
          label: t(`identityReviews.filters.${value}`),
          count: value === 'all' ? rows.length : filterReviews(rows, value).length,
        }))}
      />

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : visibleRows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
          {rows.length === 0 ? t('identityReviews.empty') : t('identityReviews.emptyFiltered')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {visibleRows.map((row) => (
            <li
              key={row.userId}
              className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-3"
            >
              <Button
                type="button"
                variant="ghost"
                className="h-20 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted p-0"
                onClick={() => setOpenRow(row)}
                aria-label={t('identityReviews.actions.openPhoto')}
              >
                {row.photoUrl ? (
                  <img src={row.photoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-[10px] uppercase text-muted-foreground">
                    {t('identityReviews.noPhoto')}
                  </span>
                )}
              </Button>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{studentLabel(row) || t('identityReviews.unnamedStudent')}</span>
                  <IdentityStatusChip status={row.identityStatus} />
                  {row.photoKind && (
                    <Badge variant="outline">{t(`identityReviews.photoKind.${row.photoKind}`)}</Badge>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {row.email}
                  {row.createdAt ? ` · ${t('identityReviews.signedUpAt', { date: formatDate(row.createdAt) })}` : ''}
                </span>
                <span className="text-sm">{verdictLine(row)}</span>
                {row.aiVerdict?.reason ? (
                  <span className="text-xs italic text-muted-foreground">“{row.aiVerdict.reason}”</span>
                ) : null}
                <span className="text-xs text-muted-foreground">
                  {t('identityReviews.attempts', { count: row.attempts })}
                  {row.checkedAt ? ` · ${t('identityReviews.checkedAt', { date: formatDate(row.checkedAt) })}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpenRow(row)}>
                  {t('identityReviews.actions.review')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={openRow !== null} onOpenChange={(open) => { if (!open) setOpenRow(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {openRow && (
            <>
              <DialogHeader>
                <DialogTitle>{studentLabel(openRow) || t('identityReviews.unnamedStudent')}</DialogTitle>
                <DialogDescription>
                  {openRow.email}
                  {openRow.photoKind ? ` · ${t(`identityReviews.photoKind.${openRow.photoKind}`)}` : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4 md:flex-row">
                <div className="flex-1 overflow-hidden rounded-md border border-border bg-muted">
                  {openRow.photoUrl ? (
                    <img src={openRow.photoUrl} alt="" className="max-h-[60vh] w-full object-contain" />
                  ) : (
                    <p className="p-10 text-center text-sm text-muted-foreground">{t('identityReviews.noPhoto')}</p>
                  )}
                </div>
                <div className="flex w-full flex-col gap-2 text-sm md:w-64">
                  <IdentityStatusChip status={openRow.identityStatus} />
                  <span>{verdictLine(openRow)}</span>
                  {openRow.aiVerdict?.reason ? (
                    <span className="text-xs italic text-muted-foreground">“{openRow.aiVerdict.reason}”</span>
                  ) : null}
                  {openRow.aiVerdict?.model ? (
                    <span className="text-xs text-muted-foreground">{openRow.aiVerdict.model}</span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">
                    {t('identityReviews.attempts', { count: openRow.attempts })}
                  </span>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="ghost"
                  className="text-muted-foreground hover:text-red-600"
                  disabled={reviewMutation.isPending}
                  onClick={() => setPendingDecline(openRow)}
                >
                  {t('identityReviews.actions.decline')}
                </Button>
                <Button
                  onClick={() => decide(openRow, 'approve')}
                  isLoading={reviewMutation.isPending}
                  loadingText={t('common.status.saving')}
                >
                  {t('identityReviews.actions.approve')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDecline !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDecline(null);
        }}
        description={t('identityReviews.actions.declineConfirm', { name: pendingDecline ? studentLabel(pendingDecline) : '' })}
        confirmLabel={t('identityReviews.actions.decline')}
        destructive
        onConfirm={() => {
          const row = pendingDecline;
          setPendingDecline(null);
          if (row) void decide(row, 'decline');
        }}
      />
    </div>
  );
}
